import React, { useState } from 'react';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { toast } from 'react-toastify';
import './EmergencyAccountRecovery.css';

const EmergencyAccountRecovery = () => {
  const [userId, setUserId] = useState('');
  const [recoveryData, setRecoveryData] = useState({
    pseudo: '',
    dp: 0,
    YP: 0,
    facebookLink: '',
    masterDuelId: '',
    edoproPseudo: '',
    statsVisibility: {
      matches: true,
      wins: true,
      losses: true,
      champion: true,
    },
    notificationsEnabled: true
  });
  const [loading, setLoading] = useState(false);
  const [currentData, setCurrentData] = useState(null);

  const fetchCurrentData = async () => {
    if (!userId) {
      toast.error('Please enter a User ID');
      return;
    }

    setLoading(true);
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const data = userSnap.data();
        setCurrentData(data);
        setRecoveryData({
          pseudo: data.pseudo || '',
          dp: data.dp || 0,
          YP: data.YP || 0,
          facebookLink: data.facebookLink || '',
          masterDuelId: data.masterDuelId || '',
          edoproPseudo: data.edoproPseudo || '',
          statsVisibility: data.statsVisibility || {
            matches: true,
            wins: true,
            losses: true,
            champion: true,
          },
          notificationsEnabled: data.notificationsEnabled !== false
        });
        toast.success('Current data loaded');
      } else {
        toast.error('User not found');
        setCurrentData(null);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      toast.error('Failed to fetch user data');
    } finally {
      setLoading(false);
    }
  };

  const restoreAccount = async () => {
    if (!userId) {
      toast.error('Please enter a User ID');
      return;
    }

    if (!window.confirm('⚠️ Are you sure you want to restore this account? This will overwrite current data!')) {
      return;
    }

    setLoading(true);
    try {
      // Use backup wrapper for emergency recovery
      const { updateUserProfileWithBackup } = await import('../utils/clientBackupWrapper');
      
      const restoredData = {
        ...recoveryData,
        lastLoginTime: new Date().toISOString(),
        restoredAt: new Date().toISOString(),
        restoredBy: 'Emergency Recovery Tool'
      };

      await updateUserProfileWithBackup(userId, restoredData, 'emergency_recovery', {
        restoredFields: Object.keys(restoredData),
        previousData: currentData,
        restoredData: restoredData,
        restoredBy: 'Emergency Recovery Tool'
      });
      
      toast.success('✅ Account restored successfully!');
      console.log('Account restored:', restoredData);
    } catch (error) {
      console.error('Error restoring account:', error);
      toast.error('Failed to restore account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="emergency-recovery">
      <div className="recovery-header">
        <h2>🚨 Emergency Account Recovery</h2>
        <p>Use this tool to restore account data that was accidentally reset.</p>
      </div>

      <div className="recovery-form">
        <div className="form-group">
          <label>User ID (UID)</label>
          <input
            type="text"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            placeholder="Enter the user's UID"
          />
          <button onClick={fetchCurrentData} disabled={loading}>
            {loading ? 'Loading...' : 'Load Current Data'}
          </button>
        </div>

        {currentData && (
          <div className="current-data">
            <h3>Current Account Data:</h3>
            <div className="data-display">
              <p><strong>Pseudo:</strong> {currentData.pseudo || 'Not set'}</p>
              <p><strong>DP:</strong> {currentData.dp || 0}</p>
              <p><strong>YP:</strong> {currentData.YP || 0}</p>
              <p><strong>Facebook Link:</strong> {currentData.facebookLink || 'Not set'}</p>
              <p><strong>Master Duel ID:</strong> {currentData.masterDuelId || 'Not set'}</p>
              <p><strong>EDOPro Pseudo:</strong> {currentData.edoproPseudo || 'Not set'}</p>
            </div>
          </div>
        )}

        <div className="recovery-fields">
          <h3>Restore Data:</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label>Pseudo</label>
              <input
                type="text"
                value={recoveryData.pseudo}
                onChange={(e) => setRecoveryData(prev => ({ ...prev, pseudo: e.target.value }))}
                placeholder="Enter pseudo"
              />
            </div>
            
            <div className="form-group">
              <label>DP (Duel Points)</label>
              <input
                type="number"
                value={recoveryData.dp}
                onChange={(e) => setRecoveryData(prev => ({ ...prev, dp: parseInt(e.target.value) || 0 }))}
                placeholder="Enter DP amount"
              />
            </div>
            
            <div className="form-group">
              <label>YP (YME Points)</label>
              <input
                type="number"
                value={recoveryData.YP}
                onChange={(e) => setRecoveryData(prev => ({ ...prev, YP: parseInt(e.target.value) || 0 }))}
                placeholder="Enter YP amount"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Facebook Link</label>
              <input
                type="url"
                value={recoveryData.facebookLink}
                onChange={(e) => setRecoveryData(prev => ({ ...prev, facebookLink: e.target.value }))}
                placeholder="Enter Facebook link"
              />
            </div>
            
            <div className="form-group">
              <label>Master Duel ID</label>
              <input
                type="text"
                value={recoveryData.masterDuelId}
                onChange={(e) => setRecoveryData(prev => ({ ...prev, masterDuelId: e.target.value }))}
                placeholder="Enter Master Duel ID"
              />
            </div>
            
            <div className="form-group">
              <label>EDOPro Pseudo</label>
              <input
                type="text"
                value={recoveryData.edoproPseudo}
                onChange={(e) => setRecoveryData(prev => ({ ...prev, edoproPseudo: e.target.value }))}
                placeholder="Enter EDOPro pseudo"
              />
            </div>
          </div>
        </div>

        <div className="recovery-actions">
          <button 
            className="restore-button" 
            onClick={restoreAccount}
            disabled={loading || !userId}
          >
            {loading ? 'Restoring...' : '🚨 Restore Account'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmergencyAccountRecovery;
